import { useCallback, useEffect, useRef, useState } from "react";
import { Minus, Plus, RotateCcw } from "lucide-react";
import { CardImage } from "@/components/CardImage";
import { CardAlertButtons } from "@/components/CardAlertButtons";

const MIN = 1;
const MAX = 5;

function clamp(n: number, lo: number, hi: number) {
  return Math.min(hi, Math.max(lo, n));
}

/**
 * Zoom-and-pan viewer for an official card scan. Wheel/pinch zoom is anchored on
 * the cursor, drag pans, and the +/- controls zoom about the centre so keyboard
 * and touch users get the same reach.
 */
export function CardZoom({
  src,
  alt,
  caption,
  cardCode,
  cardName,
  className = "",
}: {
  src: string;
  alt: string;
  caption?: string;
  cardCode?: string;
  cardName?: string;
  className?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const drag = useRef<{ x: number; y: number; ox: number; oy: number } | null>(null);
  const dims = useRef({ width: 420, height: 586 });

  const state = useRef({ zoom, offset });
  state.current = { zoom, offset };

  const zoomAt = useCallback((next: number, px: number, py: number) => {
    const { zoom: z, offset: o } = state.current;
    const target = clamp(next, MIN, MAX);
    const k = target / z;
    setZoom(target);
    setOffset(target === 1 ? { x: 0, y: 0 } : { x: px - (px - o.x) * k, y: py - (py - o.y) * k });
  }, []);

  const wheelRef = useRef<(e: WheelEvent) => void>(() => {});
  wheelRef.current = (e: WheelEvent) => {
    const dy = e.deltaY * (e.deltaMode === 1 ? 16 : e.deltaMode === 2 ? 100 : 1);
    zoomAt(state.current.zoom * Math.exp(-dy * 0.0018), e.offsetX, e.offsetY);
  };

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;
      dims.current = { width, height };
    });
    ro.observe(el);
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      wheelRef.current(e);
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => {
      ro.disconnect();
      el.removeEventListener("wheel", onWheel);
    };
  }, []);

  const step = (dir: 1 | -1) => {
    zoomAt(state.current.zoom * (dir === 1 ? 1.4 : 1 / 1.4), dims.current.width / 2, dims.current.height / 2);
  };

  const reset = () => {
    setZoom(1);
    setOffset({ x: 0, y: 0 });
  };

  return (
    <figure className={className}>
      <div
        ref={ref}
        className="relative overflow-hidden rounded-xl border border-border/60 bg-background/60"
        style={{ touchAction: "none", cursor: drag.current ? "grabbing" : "grab" }}
        onPointerDown={(e) => {
          // Panning works at any zoom level so the card can always be slid
          // side to side by hand.
          (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
          drag.current = { x: e.clientX, y: e.clientY, ox: offset.x, oy: offset.y };
        }}
        onPointerMove={(e) => {
          const d = drag.current;
          if (!d) return;
          setOffset({ x: d.ox + (e.clientX - d.x), y: d.oy + (e.clientY - d.y) });
        }}
        onPointerUp={() => {
          drag.current = null;
        }}
        onDoubleClick={(e) => {
          if (zoom > 1) reset();
          else zoomAt(2.5, e.nativeEvent.offsetX, e.nativeEvent.offsetY);
        }}
      >
        <div
          style={{
            transform: `translate(${offset.x}px, ${offset.y}px) scale(${zoom})`,
            transformOrigin: "0 0",
            transition: drag.current ? "none" : "transform 120ms ease-out",
          }}
        >
          <CardImage src={src} alt={alt} width={420} height={586} responsive className="w-full select-none" />
        </div>
        <div className="absolute bottom-2 right-2 flex items-center gap-1 rounded-lg border border-border/60 bg-background/85 p-1 backdrop-blur">
          <button type="button" onClick={() => step(-1)} aria-label="Zoom out" className="rounded p-1 hover:bg-card">
            <Minus className="h-3.5 w-3.5" />
          </button>
          <span className="w-9 text-center text-[10px] font-semibold tabular-nums">{zoom.toFixed(1)}×</span>
          <button type="button" onClick={() => step(1)} aria-label="Zoom in" className="rounded p-1 hover:bg-card">
            <Plus className="h-3.5 w-3.5" />
          </button>
          <button type="button" onClick={reset} aria-label="Reset zoom" className="rounded p-1 hover:bg-card">
            <RotateCcw className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
      {caption && <figcaption className="mt-1.5 text-center text-[11px] text-muted-foreground">{caption}</figcaption>}
      {cardCode && cardName ? <CardAlertButtons code={cardCode} name={cardName} compact /> : null}
    </figure>
  );
}
